import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, IconButton, Select, MenuItem, FormControl, Box, InputLabel } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ToolMenu from './ToolMenu';
import { useTranslation } from 'react-i18next';

export default function ToolCard({ tool, onLaunch, onDeleteRequest, onSettings, onHistory, onUpdateProviderModel, user }) {
    const { t } = useTranslation();

    const [menuAnchor, setMenuAnchor] = useState(null);

    // Get effective provider/model (tool-specific or user default)
    const effectiveProvider = tool.provider || user?.provider || 'openai';
    const effectiveModel = tool.model || user?.model || 'gpt-4';

    // Get available providers and models from user's allowed list
    const availableProviders = user?.allowed ? Object.keys(user.allowed) : ['openai'];
    const availableModels = user?.allowed?.[effectiveProvider] || [effectiveModel];

    const handleProviderChange = (e) => {
        const newProvider = e.target.value;
        const newModels = user?.allowed?.[newProvider] || [];
        const newModel = newModels[0] || effectiveModel;
        onUpdateProviderModel(tool.id, newProvider, newModel);
    };

    const handleModelChange = (e) => {
        const newModel = e.target.value;
        onUpdateProviderModel(tool.id, effectiveProvider, newModel);
    };

    return (
        <Card
            elevation={3}
            sx={{
                width: '260px',
                minHeight: '240px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                mx: 'auto',
                p: 2,
                position: 'relative',
            }}
        >
            <IconButton
                size="small"
                sx={{ position: 'absolute', top: 4, right: 4 }}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>

            <CardContent
                sx={{
                    p: 0,
                    '&:last-child': { pb: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flexGrow: 1,
                    width: '100%',
                }}
            >
                {/* Title - single line, ellipsis */}
                <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{ width: '100%', minHeight: '24px', fontWeight: 600 }}
                >
                    {tool.name}
                </Typography>

                {/* Description - up to 2 lines, ellipsis */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        width: '100%',
                        mt: 0.5,
                        minHeight: '36px',
                    }}
                >
                    {tool.description}
                </Typography>

                {/* Provider and Model Dropdowns */}
                <Box sx={{
                    mt: 1.5,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1
                }}>
                    <FormControl size="small" fullWidth variant="standard">
                        <Select
                            value={effectiveProvider}
                            onChange={handleProviderChange}
                            disableUnderline
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                                fontSize: '0.875rem',
                                color: 'text.secondary',
                                '& .MuiSelect-select': {
                                    py: 0.25,
                                    px: 0,
                                    fontWeight: 500,
                                },
                                '& .MuiSelect-icon': {
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    color: 'text.secondary',
                                },
                                '&:hover .MuiSelect-icon': {
                                    opacity: 1,
                                }
                            }}
                        >
                            {availableProviders.map((p) => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth variant="standard">
                        <Select
                            value={effectiveModel}
                            onChange={handleModelChange}
                            disableUnderline
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                                fontSize: '0.875rem',
                                color: 'text.secondary',
                                '& .MuiSelect-select': {
                                    py: 0.25,
                                    px: 0,
                                    fontWeight: 500,
                                },
                                '& .MuiSelect-icon': {
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    color: 'text.secondary',
                                },
                                '&:hover .MuiSelect-icon': {
                                    opacity: 1,
                                }
                            }}
                        >
                            {availableModels.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* History button (clickable item) */}
                <Button
                    variant="text"
                    color="secondary"
                    onClick={onHistory}
                    startIcon={<HistoryIcon />}
                    sx={{ mt: 1.5, p: 0, minWidth: 'auto' }}
                >
                    {t('history')}
                </Button>

                {/* Launch button */}
                <Button
                    variant="text"
                    color="primary"
                    onClick={onLaunch}
                    startIcon={<PlayArrowIcon />}
                    sx={{ mt: 0.5, p: 0, mb: 0.5, minWidth: 'auto' }}
                >
                    {t('launch_tool')}
                </Button>
            </CardContent>

            <ToolMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onSettings={onSettings}
                onDelete={onDeleteRequest}
            />
        </Card>
    );
}
